import { z } from "zod";
import { useMutation } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { DataListOptionValues } from "./useGetAllDataListValueOverride";
import { mergeDataListValueOverrideChildren } from "./mergeDataListValueOverrideChildren";

const baseUrl = "/api/data_list_version_value_overrides" as const;

type HydraCollection<T> = {
    "hydra:member"?: T[];
};

type ExistingOverrideSummary = {
    id: number;
    dataListOptionValueOverrides: DataListOptionValueOverrideWrite[];
    dataListExtraOptions: DataListExtraOptionWrite[];
};

export const upsertDataListValueOverride = async (override: DataListValueOverrideWrite) => {
    if (override.id !== undefined) {
        return loggedInApiCall.put(`${baseUrl}/${override.id}`, override);
    }

    const existingResponse = await loggedInApiCall.get<HydraCollection<{ id: number }>>(baseUrl, {
        params: {
            dataListVersionId: override.dataListVersionId,
            itemsPerPage: 1,
        },
    });

    const existingId = existingResponse.data["hydra:member"]?.[0]?.id;

    if (existingId === undefined) {
        return loggedInApiCall.post(baseUrl, override);
    }

    const existingItemResponse = await loggedInApiCall.get<ExistingOverrideSummary>(`${baseUrl}/${existingId}`);
    const mergedChildren = mergeDataListValueOverrideChildren(existingItemResponse.data, override);

    return loggedInApiCall.put(`${baseUrl}/${existingId}`, {
        ...override,
        id: existingId,
        ...mergedChildren,
    });
};

export const useUpsertDataListValueOverride = () => {
    return useMutation({
        mutationFn: upsertDataListValueOverride,
    });
};

export const DataListOptionValueOverrideWriteSchema = DataListOptionValues.extend({
    id: z.number().optional(),
    "@id": z.string().optional(),
    dataListOptionCode: z.string(),
});

export const DataListExtraOptionWriteSchema = DataListOptionValues.extend({
    id: z.number().optional(),
    "@id": z.string().optional(),
    code: z.string(),
    label: z.string(),
});

export const DataListValueOverrideWriteSchema = z.object({
    id: z.number().optional(),
    dataListVersionId: z.number(),
    dataListOptionValueOverrides: DataListOptionValueOverrideWriteSchema.array(),
    dataListExtraOptions: DataListExtraOptionWriteSchema.array(),
});

export type DataListOptionValueOverrideWrite = z.infer<typeof DataListOptionValueOverrideWriteSchema>;
export type DataListExtraOptionWrite = z.infer<typeof DataListExtraOptionWriteSchema>;
export type DataListValueOverrideWrite = z.infer<typeof DataListValueOverrideWriteSchema>;
